import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CTABand from "@/components/CTABand";
import { Reveal } from "@/lib/motion";
import api from "@/lib/api";
import { ZODIAC, IMAGES } from "@/lib/content";
import { toast } from "sonner";
import ZodiacWheel from "@/components/ZodiacWheel";
import HoroscopeResult from "@/components/HoroscopeResult";

export default function Horoscope() {
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const pick = async (sign) => {
    setSelected(sign.key);
    setData(null);
    setLoading(true);
    try {
      const res = await api.get(`/horoscope/${sign.key}`);
      setData(res.data);
    } catch (e) {
      toast.error("Horoscope is resting. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const current = ZODIAC.find((z) => z.key === selected);

  return (
    <div className="relative z-10">
      <PageHeader
        overline="AI-Powered Daily Guidance"
        titleLines={["Your Daily", <span key="2" className="text-gold italic">Horoscope</span>]}
        subtitle="Select your zodiac sign to reveal today's personalised, AI-crafted reading across every area of life."
        image={IMAGES.mystic}
      />

      <section className="py-10 md:py-16 relative overflow-hidden">
        <ZodiacWheel className="absolute left-1/2 -translate-x-1/2 top-10 w-[900px] h-[900px] opacity-[0.06] pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 md:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="overline text-gold mb-6">Choose Your Sign</p>
                <div className="grid grid-cols-3 gap-4" data-testid="zodiac-grid">
                  {ZODIAC.map((z) => (
                    <button
                      key={z.key}
                      onClick={() => pick(z)}
                      data-testid={`zodiac-${z.key}`}
                      className={`zodiac-orbit group aspect-square rounded-2xl border flex flex-col items-center justify-center gap-1 ${selected === z.key ? "border-gold bg-gold/10 -translate-y-1" : "border-white/10 hover:border-gold/40 hover:-translate-y-1"}`}
                    >
                      <span className={`text-3xl ${selected === z.key ? "text-gold" : "text-white/80 group-hover:text-gold"}`}>{z.symbol}</span>
                      <span className="font-display text-base">{z.name}</span>
                      <span className="text-[10px] text-[#64748B]">{z.dates}</span>
                    </button>
                  ))}
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <div className="glass rounded-3xl p-8 md:p-10 min-h-[480px]" data-testid="horoscope-panel">
                <AnimatePresence mode="wait">
                  {!selected && (
                    <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col items-center justify-center text-center py-20">
                      <Sparkles className="text-gold mb-5" size={40} />
                      <h3 className="font-display text-3xl">The stars await</h3>
                      <p className="text-[#94A3B8] mt-3 max-w-sm">Select your zodiac sign to unveil today&apos;s reading, freshly generated for you.</p>
                    </motion.div>
                  )}
                  {selected && loading && (
                    <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col items-center justify-center py-20">
                      <Loader2 className="text-gold animate-spin mb-5" size={40} />
                      <p className="text-[#94A3B8]">Consulting the cosmos for {current?.name}...</p>
                    </motion.div>
                  )}
                  {selected && !loading && data && (
                    <motion.div key="data" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                      <HoroscopeResult data={data} sign={current} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </section>
      <CTABand />
    </div>
  );
}
